import type React from "react"
import { useEffect, useState, useCallback } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import {
  ArrowLeft,
} from "lucide-react"
import { Button } from "../../components/ui/button"
import { Separator } from "../../components/ui/separator"
import UserInformationCard from "../../components/forms/UserInformationCard"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog"
import { DeleteModal } from "../../components/modals/DeleteModal"
import { usersApi } from "../../services/usersApi"

interface UserDetailsData {
  id: string
  first_name: string
  last_name: string
  email: string
  is_active: boolean
  is_staff: boolean
  is_superuser: boolean
  created_at?: string
  updated_at?: string
}

export default function UserDetails() {
  const { state } = useLocation()
  const navigate = useNavigate()
  const [userDetails, setUserDetails] = useState<UserDetailsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [editingData, setEditingData] = useState<UserDetailsData | null>(null)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [deleteUserModal, setDeleteUserModal] = useState(false)
  const [selectedId, setSelectedId] = useState("")
  const [editErrors, setEditErrors] = useState<{ [key: string]: string[] }>({})

  useEffect(() => {
    if (state?.userId) {
      fetchUserDetails(state.userId)
    } else {
      // No userId provided, redirect back to users list
      navigate("/app/users")
    }
  }, [state?.userId, navigate]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUserDetails = useCallback(async (userId: string) => {
    try {
      setLoading(true)
      const response = await usersApi.getUserById(userId)
      
      // Transform the response to match our interface
      const userData: UserDetailsData = {
        id: response.id,
        first_name: response.first_name,
        last_name: response.last_name,
        email: response.email,
        is_active: response.is_active,
        is_staff: response.is_staff,
        is_superuser: response.is_superuser,
      }
      
      setUserDetails(userData)
    } catch (error) {
      console.error('Failed to fetch user details:', error)
      setUserDetails(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleBack = () => {
    navigate("/app/users")
  }

  const handleEdit = () => {
    setIsEditing(true)
    setEditingData(userDetails)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditingData(null)
    setEditErrors({})
  }

  const handleSaveEdit = (data: any) => {
    // Validate required fields
    const errors: { [key: string]: string[] } = {}
    
    if (!data.first_name?.trim()) {
      errors.first_name = ["First name is required"]
    }
    
    if (!data.last_name?.trim()) {
      errors.last_name = ["Last name is required"]
    }
    
    if (!data.email?.trim()) {
      errors.email = ["Email is required"]
    } else {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(data.email)) {
        errors.email = ["Please enter a valid email address"]
      }
    }
    
    if (Object.keys(errors).length > 0) {
      setEditErrors(errors)
      return
    }
    
    // Clear errors if validation passes
    setEditErrors({})
    setShowSaveDialog(true)
  }

  const confirmSave = async () => {
    if (!editingData || !userDetails) {
      setShowSaveDialog(false)
      return
    }

    try {
      // Prepare the data for the API call
      const updateData = {
        first_name: editingData.first_name,
        last_name: editingData.last_name,
        email: editingData.email,
        is_active: editingData.is_active,
        is_staff: editingData.is_staff,
        is_superuser: editingData.is_superuser,
      }

      // Make the API call to update the user
      const response = await usersApi.updateUser(userDetails.id, updateData)
            
      // Update the local state with the response data
      if (response.user) {
        const updatedUser: UserDetailsData = {
          id: response.user.id,
          first_name: response.user.first_name,
          last_name: response.user.last_name,
          email: response.user.email,
          is_active: response.user.is_active,
          is_staff: response.user.is_staff,
          is_superuser: response.user.is_superuser,
        }
        setUserDetails(updatedUser)
      } else {
        // Fallback: update with editing data
        setUserDetails(editingData)
      }
      
      // Reset editing state
      setIsEditing(false)
      setEditingData(null)
      setShowSaveDialog(false)
      setEditErrors({})
      
    } catch (error) {
      console.error('Failed to update user:', error)
      // Handle API errors - you might want to show a toast notification here
      // For now, we'll keep the dialog open and let the user try again
    }
  }

  const cancelSave = () => {
    setShowSaveDialog(false)
  }



  const deleteUserModalClose = () => {
    setDeleteUserModal(false)
    setSelectedId("")
  }

  const deleteItem = async () => {
    try {
      await usersApi.deleteUser(selectedId)
      deleteUserModalClose()
      navigate("/app/users")
    } catch (error) {
      console.error('Failed to delete user:', error)
    }
  }

  const handleDataChange = (field: string, value: any) => {
    if (editingData) {
      setEditingData({ ...editingData, [field]: value })
      // Clear error for this field when user starts typing
      if (editErrors[field]) {
        setEditErrors(prev => ({ ...prev, [field]: [] }))
      }
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
          <p className="mt-2 text-slate-600">Loading user details...</p>
        </div>
      </div>
    )
  }

  if (!userDetails) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600">User not found</p>
          <Button onClick={handleBack} className="mt-4">
            Back to Users
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={handleBack} className="text-slate-600 hover:text-slate-900">
                <ArrowLeft className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Back to Users</span>
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <h1 className="text-2xl font-bold text-slate-900">User Details</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto">
          {/* User Information */}
          <UserInformationCard
            userData={{
              first_name: editingData ? editingData.first_name : userDetails.first_name,
              last_name: editingData ? editingData.last_name : userDetails.last_name,
              email: editingData ? editingData.email : userDetails.email,
            }}
            errors={editErrors}
            onDataChange={handleDataChange}
            viewOnly={!isEditing}
            isEditing={isEditing}
            onEdit={handleEdit}
            onSave={handleSaveEdit}
            onCancel={handleCancelEdit}
            isNewUser={false}
            userId={userDetails.id}
          />
        </div>

        {/* Save Confirmation Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Changes</DialogTitle>
              <DialogDescription>
                Are you sure you want to save the changes to this user? This will update the user information permanently.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={cancelSave}>
                Cancel
              </Button>
              <Button onClick={confirmSave} className="bg-green-600 hover:bg-green-700">
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <DeleteModal
          open={deleteUserModal}
          onClose={deleteUserModalClose}
          onConfirm={deleteItem}
          title="Delete User"
          description="Are you sure you want to delete this user? This action cannot be undone."
        />
      </div>
    </div>
  )
} 